import {useState} from 'react';
import {Alert, Form, Input, Select, Typography} from 'antd';
import {useMutation} from '@apollo/client';
import {toast} from 'react-toastify';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {Button} from '@dabafinance/react-components';
import {
  REVOKE_REFERRAL_PARTNER,
  RESET_REFERRAL_PARTNER_PASSWORD,
  EXPORT_REFERRAL_PARTNERS,
} from './resolvers';
import {isOfficialEmail, OFFICIAL_EMAIL_DOMAINS} from './utils';

const {Text} = Typography;

const partnerLabel = partner =>
  `${partner?.firstName || ''} ${partner?.lastName || ''}`.trim() ||
  partner?.email ||
  'this partner';

/**
 * Revoke is a soft delete: the partner type goes back to NONE, the portal
 * account is locked and their coupons are disabled. Referees and earnings
 * history are kept, and the partner can be restored by editing them again.
 */
export const RevokePartnerContent = ({partner, onDone, onClose}) => {
  const [blockingError, setBlockingError] = useState(null);
  const [result, setResult] = useState(null);

  const [revokePartner, {loading}] = useMutation(REVOKE_REFERRAL_PARTNER, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  const handleRevoke = force => {
    setBlockingError(null);
    revokePartner({
      variables: {
        data: {
          partnerId: partner?.id,
          ...(force ? {force: true} : {}),
        },
      },
    })
      .then(({data}) => {
        const response = data?.adminDeleteReferralPartner;
        if (response?.__typename === 'Error') {
          // Without `force` the API refuses while unpaid earnings exist, and
          // the message names the count and the total.
          if (!force) {
            setBlockingError(response.message);
          } else {
            toast.error(response.message);
          }
          return;
        }
        setResult(response);
        onDone?.();
      })
      .catch(error => toast.error(error.message || 'Something went wrong'));
  };

  if (result) {
    return (
      <div>
        <h2>Partner Revoked</h2>
        <Text type="secondary">{result.message}</Text>
        <ul style={{marginTop: 16, lineHeight: 2}}>
          <li>
            Referees kept on the account:{' '}
            <strong>{result.totalReferees ?? 0}</strong>
          </li>
          <li>
            Unpaid earning line items:{' '}
            <strong>{result.pendingEarningsCount ?? 0}</strong>
          </li>
          <li>
            Unpaid earnings total:{' '}
            <strong>
              {Number(result.pendingEarningsAmount || 0).toLocaleString(
                'en-US',
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                },
              )}
            </strong>
          </li>
          <li>
            Promo codes disabled: <strong>{result.couponsDisabled ?? 0}</strong>
          </li>
        </ul>
        <Alert
          type="info"
          showIcon
          message="Any promo codes this partner owned have been disabled, so no further Academy sale is attributed to them. Settle the unpaid earnings above separately."
          style={{marginTop: 8}}
        />
        <div
          style={{display: 'flex', justifyContent: 'flex-end', marginTop: 24}}>
          <Button label="Done" onClick={onClose} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2>Revoke {partnerLabel(partner)}?</h2>
      <Text type="secondary">
        This sets their partner type back to None, locks their portal account
        and disables the promo codes they own. Their referees and earnings
        history are kept, and you can restore them later by editing the partner.
      </Text>

      {blockingError && (
        <Alert
          type="error"
          showIcon
          style={{marginTop: 16}}
          message="This partner still has unpaid earnings"
          description={blockingError}
        />
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 10,
          marginTop: 24,
        }}>
        <Button type="secondary" label="Cancel" onClick={onClose} />
        {blockingError ? (
          <Button
            disabled={loading}
            label="Revoke anyway"
            onClick={() => handleRevoke(true)}
          />
        ) : (
          <Button
            disabled={loading}
            label="Revoke"
            onClick={() => handleRevoke(false)}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Partners sign in to their own portal. `adminUpdateReferralPartner` never
 * touches the password, so this is the only recovery path.
 */
export const ResetPartnerPasswordContent = ({partner, onClose}) => {
  const [form] = Form.useForm();

  const [resetPassword, {loading}] = useMutation(
    RESET_REFERRAL_PARTNER_PASSWORD,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const handleSubmit = values => {
    resetPassword({
      variables: {
        data: {
          partnerId: partner?.id,
          password: values.password,
        },
      },
    })
      .then(({data}) => {
        const response = data?.adminResetReferralPartnerPassword;
        if (response?.__typename === 'Error') {
          return toast.error(response.message);
        }
        toast.success(response?.message || 'Portal password reset');
        form.resetFields();
        onClose();
      })
      .catch(error => toast.error(error.message || 'Something went wrong'));
  };

  return (
    <div>
      <h2>Reset Portal Password</h2>
      <Text type="secondary">
        Sets a new sign-in password for {partnerLabel(partner)}. Share it with
        them over a secure channel — it is never shown again after you submit.
      </Text>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{marginTop: 16}}>
        <Form.Item
          label="New Password"
          name="password"
          rules={[
            {required: true, message: 'Password is required'},
            {min: 8, message: 'Password must be at least 8 characters'},
          ]}>
          <Input.Password placeholder="At least 8 characters" />
        </Form.Item>
        <Form.Item
          label="Confirm New Password"
          name="confirmPassword"
          dependencies={['password']}
          rules={[
            {required: true, message: 'Please confirm the password'},
            ({getFieldValue}) => ({
              validator: (_, value) =>
                !value || getFieldValue('password') === value
                  ? Promise.resolve()
                  : Promise.reject(new Error('Passwords do not match')),
            }),
          ]}>
          <Input.Password placeholder="Re-enter the password" />
        </Form.Item>
        <div
          style={{display: 'flex', justifyContent: 'flex-end', marginTop: 24}}>
          <SubmitButton label="Reset Password" disabled={loading} />
        </div>
      </Form>
    </div>
  );
};

/** Async CSV export — the file is emailed, there is nothing to download here. */
export const ExportPartnersContent = ({filters, filterSummary, onClose}) => {
  const [form] = Form.useForm();

  const [exportPartners, {loading}] = useMutation(EXPORT_REFERRAL_PARTNERS, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  const handleSubmit = values => {
    const emails = values.emails || [];
    const invalid = emails.filter(email => !isOfficialEmail(email));
    if (invalid.length) {
      return toast.error(
        `These addresses are not allowed: ${invalid.join(
          ', ',
        )}. Recipients must be ${OFFICIAL_EMAIL_DOMAINS.map(
          domain => `@${domain}`,
        ).join(' or ')} addresses.`,
      );
    }

    exportPartners({
      variables: {
        data: {
          ...(Object.keys(filters || {}).length ? {filters} : {}),
          ...(emails.length ? {emails} : {}),
        },
      },
    })
      .then(({data}) => {
        const response = data?.adminExportReferralPartners;
        if (response?.__typename === 'Error') {
          return toast.error(response.message);
        }
        toast.success(
          response?.message ||
            "Your export is being prepared — you'll receive it by email.",
        );
        form.resetFields();
        onClose();
      })
      .catch(error => toast.error(error.message || 'Something went wrong'));
  };

  return (
    <div>
      <h2>Export Referral Partners</h2>
      <Text type="secondary">
        Exports the partners matching the filters currently applied to the list.
        The CSV is generated in the background and emailed to you — there is
        nothing to download here.
      </Text>

      <Alert
        type="info"
        showIcon
        style={{marginTop: 16}}
        message={
          filterSummary || 'No filters applied — exporting all partners.'
        }
      />

      <Alert
        type="warning"
        showIcon
        style={{marginTop: 12}}
        message="Commission figures roll up on the 1st of each month, so a mid-month export shows zeros in the money columns."
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{marginTop: 16}}>
        <Form.Item
          label="Additional Recipients (optional)"
          name="emails"
          extra={`You are always included. Others must be ${OFFICIAL_EMAIL_DOMAINS.map(
            domain => `@${domain}`,
          ).join(' or ')} addresses.`}
          rules={[
            {
              validator: (_, value) => {
                const invalid = (value || []).filter(
                  email => !isOfficialEmail(email),
                );
                return invalid.length
                  ? Promise.reject(
                      new Error(`Not allowed: ${invalid.join(', ')}`),
                    )
                  : Promise.resolve();
              },
            },
          ]}>
          <Select
            mode="tags"
            tokenSeparators={[',', ' ']}
            placeholder="name@joindaba.com"
            open={false}
          />
        </Form.Item>
        <div
          style={{display: 'flex', justifyContent: 'flex-end', marginTop: 24}}>
          <SubmitButton label="Request Export" disabled={loading} />
        </div>
      </Form>
    </div>
  );
};
